import React from 'react';
import PropTypes from 'prop-types';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

import PdfStyles from './PdfStyles';
import PdfFooter from '../../../shared/components/Pdf/PdfFooter';
import PdfHeader from '../../../shared/components/Pdf/PdfHeader';

const TransactionPdfDocuement = ({ pdfDetails }) => {
  const styles = StyleSheet.create(PdfStyles);

  const remarkType = {
    cancellation: 'components.billing.invoices.remarks.cancellation',
    cancellation_penalty: 'components.billing.invoices.remarks.cancellation_penalty',
    price_decreased: 'components.billing.invoices.remarks.price_decreased',
    price_increased: 'components.billing.invoices.remarks.price_increased',
    refund: 'components.billing.invoices.remarks.refund',
    release: 'components.billing.invoices.remarks.release',
    booking: 'components.billing.invoices.remarks.booking',
    subscription_charge: 'components.billing.invoices.remarks.subscription_charge',
  };

  return (
    <Document>
      <Page size="A4" style={styles.body}>
        <PdfHeader
          welltravelName={pdfDetails.welltravelName}
          welltravelAddress1={pdfDetails.welltravelAddress1}
          welltravelAddress2={pdfDetails.welltravelAddress2}
          welltravelVat={pdfDetails.welltravelVat}
        />

        <View style={styles.subheader}>
          <View>
            <Text style={styles.invoiceID}>{pdfDetails.type}</Text>
          </View>
        </View>

        <View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.transactionForLabel}>{pdfDetails.to}</Text>
            <Text style={styles.invoiceData}>
              {pdfDetails.clientName} {pdfDetails.clientAddress}
            </Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.issueDateLabel}>{pdfDetails.issueDate}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.createdAt}</Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.issuedByLabel}>{pdfDetails.issueBy}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.welltravelName}</Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.generatedByLabel}>{pdfDetails.generatedBy}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.generatedByExecutor}</Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.transactionMethodLabel}>{pdfDetails.transactionMethod}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.transactionVia}</Text>
          </View>
          <View style={styles.invoiceDetails}>
            <Text style={styles.paidOnLabel}>{pdfDetails.paidOnLabel}</Text>
            <Text style={styles.invoiceData}>{pdfDetails.paidOn}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeaderRow}>
            <Text style={styles.tableDataFirst}>{pdfDetails.tableHeaderItem}</Text>
            {pdfDetails.subscriptionPdf && (
              <Text style={styles.tableDataFirst}>{pdfDetails.planLabel}</Text>
            )}
            {pdfDetails.transactionType === 'WelltravelBookingTransaction' && (
              <Text style={styles.tableDataFirst}>
                {I18n.t('components.billing.invoices.remarks.title')}
              </Text>
            )}
            {pdfDetails.subscriptionPdf && (
              <Text style={styles.tableDataSecond}>{pdfDetails.periodLabel}</Text>
            )}
            <Text style={styles.tableDataLast}>{pdfDetails.tableHeaderAmount}</Text>
          </View>
          <View style={styles.tableDataRow}>
            {pdfDetails.isTopUp ? (
              <Text style={styles.tableDataFirstTopUp}>
                <Text style={styles.tableDataTopUp}>{pdfDetails.lineItem}</Text>
                <Text style={styles.referenceNumber}>
                  {'\n'}
                  {pdfDetails.referenceLabel} {pdfDetails.referenceNumber}
                </Text>
              </Text>
            ) : (
              <Text style={styles.tableDataFirst}>{pdfDetails.lineItem}</Text>
            )}
            {pdfDetails.transactionType === 'WelltravelBookingTransaction' && (
              <Text style={styles.tableDataFirst}>{I18n.t(remarkType[pdfDetails.reason])}</Text>
            )}
            {pdfDetails.subscriptionPdf && (
              <Text style={styles.tableDataFirst}>{pdfDetails.planData}</Text>
            )}
            {pdfDetails.subscriptionPdf && (
              <Text style={styles.tableDataSecond}>{pdfDetails.period}</Text>
            )}
            <Text style={styles.tableDataLastAmount}>{pdfDetails.amount}</Text>
          </View>
          <View style={styles.tableSubTotalDataRow}>
            <Text style={styles.tableSubTotalData}>{pdfDetails.tableHeaderSubtotal}</Text>
            <Text style={styles.tableDataLastAmount}>{pdfDetails.amount}</Text>
          </View>
          <View style={styles.line} />
          <View style={styles.tableTotalDataRow}>
            <Text style={styles.tableTotalData}>{pdfDetails.tableTotal}</Text>
            <Text style={styles.tableTotalAmount}>{pdfDetails.amount}</Text>
          </View>
        </View>

        <View style={styles.updateDetailsHeader}>
          <Text style={styles.updateDetailsHeader}>{pdfDetails.updatedDetails}</Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeaderRow}>
            <Text style={styles.tableDataFirst}>{pdfDetails.tableHeaderItem}</Text>
            <Text style={styles.tableDataLast}>{pdfDetails.tableHeaderAmount}</Text>
          </View>
          <View style={styles.tableDataRow}>
            <Text style={styles.tableDataFirst}>{pdfDetails.initialBalance}</Text>
            <Text style={styles.tableDataLastAmount}>{pdfDetails.balance.before}</Text>
          </View>
          <View style={styles.tableDataRow}>
            <Text style={styles.tableDataFirst}>{pdfDetails.transactionLabel}</Text>
            <Text
              style={
                pdfDetails.amountPositive
                  ? styles.tableDataLastAmountPositive
                  : styles.tableDataLastAmountNegative
              }
            >
              {pdfDetails.amountPositive ? (
                <Text>
                  {pdfDetails.positive}
                  {pdfDetails.amount}
                </Text>
              ) : (
                <Text>
                  {pdfDetails.negative}
                  {pdfDetails.amount}
                </Text>
              )}
            </Text>
          </View>
          <View style={styles.newBalanceData}>
            <Text style={styles.newBalanceLabel}>{pdfDetails.newBalance}</Text>
            <Text style={styles.newBalanceAmount}>{pdfDetails.balance.after}</Text>
          </View>
        </View>

        <PdfFooter footerSupport={pdfDetails.footerSupport} page={pdfDetails.page} />
      </Page>
    </Document>
  );
};

TransactionPdfDocuement.propTypes = {
  pdfDetails: PropTypes.shape({
    welltravelName: PropTypes.string.isRequired,
    welltravelAddress1: PropTypes.string.isRequired,
    welltravelAddress2: PropTypes.string.isRequired,
    welltravelVat: PropTypes.string.isRequired,
    page: PropTypes.string.isRequired,
    footerSupport: PropTypes.string.isRequired,
    footerWebsite: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    to: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    clientAddress: PropTypes.string.isRequired,
    issueDate: PropTypes.string.isRequired,
    paidOn: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    issueBy: PropTypes.string.isRequired,
    clientNameLabel: PropTypes.string.isRequired,
    generatedBy: PropTypes.string.isRequired,
    transactionMethod: PropTypes.string.isRequired,
    tableHeaderItem: PropTypes.string.isRequired,
    paidOnLabel: PropTypes.string.isRequired,
    tableHeaderAmount: PropTypes.string.isRequired,
    tableHeaderVat: PropTypes.string.isRequired,
    tableHeaderSubtotal: PropTypes.string.isRequired,
    lineItem: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    vat: PropTypes.string.isRequired,
    total: PropTypes.string.isRequired,
    tableTotal: PropTypes.string.isRequired,
    initialBalance: PropTypes.string.isRequired,
    newBalance: PropTypes.string.isRequired,
    transactionLabel: PropTypes.string.isRequired,
    updatedDetails: PropTypes.string.isRequired,
    transaction: PropTypes.string.isRequired,
    balance: PropTypes.shape({
      after: PropTypes.string.isRequired,
      before: PropTypes.string.isRequired,
    }),
    generatedByExecutor: PropTypes.string.isRequired,
    descriptionLabel: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    transactionVia: PropTypes.string.isRequired,
    amountPositive: PropTypes.bool.isRequired,
    subscriptionPdf: PropTypes.bool.isRequired,
    planData: PropTypes.string.isRequired,
    period: PropTypes.string.isRequired,
    planLabel: PropTypes.string.isRequired,
    periodLabel: PropTypes.string.isRequired,
    referenceNumber: PropTypes.string.isRequired,
    isTopUp: PropTypes.string.isRequired,
    referenceLabel: PropTypes.string.isRequired,
    positive: PropTypes.string.isRequired,
    negative: PropTypes.string.isRequired,
    transactionType: PropTypes.string,
    reason: PropTypes.string,
  }).isRequired,
};

export default TransactionPdfDocuement;
